import { useQuery } from '@tanstack/react-query';
import { useDeliverableImportClient } from './client';
export function deliverableImportReviewKey(programId, changeSetId, workstreamKey, includeSuperseded) {
    return [
        'deliverable-import',
        programId,
        changeSetId,
        workstreamKey ?? '',
        includeSuperseded ?? false
    ];
}
/**
 * Loads server truth for a staged import. Several DeliverableImportCards in
 * one thread (e.g. a proposal-review card and its workstream-summary card)
 * share ONE fetch via this query key, per the proto doc comment: the UI
 * always renders from GetDeliverableImportReview, never from a card's own
 * fields.
 *
 * `includeSuperseded` defaults to false so every existing caller keeps
 * sharing the same cache entry; PROPOSAL_REVISED passes true for a second,
 * separate query so it can look up the proposal it supersedes without
 * pulling superseded rows into every other card's fetch.
 */ export function useDeliverableImportReview(programId, changeSetId, workstreamKey, includeSuperseded) {
    const client = useDeliverableImportClient();
    return useQuery({
        queryKey: deliverableImportReviewKey(programId, changeSetId, workstreamKey, includeSuperseded),
        queryFn: ()=>client.getDeliverableImportReview({
                programId,
                changeSetId,
                workstreamKey: workstreamKey ?? '',
                includeSuperseded: includeSuperseded ?? false
            }),
        enabled: Boolean(programId && changeSetId),
        staleTime: 0
    });
}
/**
 * Cache key a WORKSTREAM_SUMMARY card writes its
 * CommitDeliverableImportWorkstreamResponse under after a successful commit,
 * and a COMMIT_RESULT card reads from — there is no RPC to fetch a commit
 * response, so this is a plain cache slot rather than a useQuery-backed
 * fetch. Absent (e.g. after a page reload) means COMMIT_RESULT must fall
 * back to reconstructing from the review's per-proposal `commit_result`.
 */ export function deliverableImportCommitResultKey(programId, changeSetId, workstreamKey) {
    return [
        'deliverable-import-commit-result',
        programId,
        changeSetId,
        workstreamKey
    ];
}
/**
 * Invalidates every GetDeliverableImportReview query for this change set
 * (every workstream filter, both includeSuperseded variants) after a
 * Resolve/Revise call, so every DeliverableImportCard on screen — not just
 * the one that made the call — refetches and reflects the new server state.
 */ export function invalidateDeliverableImportReview(queryClient, programId, changeSetId) {
    return queryClient.invalidateQueries({
        queryKey: [
            'deliverable-import',
            programId,
            changeSetId
        ]
    });
}
